/**
 * AutoApprovalToggle — Phase R10.3 (2026-07-04)
 *
 * Simple ON/OFF toggle wired to:
 *   GET   /api/owner/brands/{brand_id}/settings
 *   PATCH /api/owner/brands/{brand_id}/auto-approval
 *
 * Optimistic UI: toggles the switch immediately on click, reverts
 * on error. Min-confidence is read-only (0.95 lock per R10 brief D2).
 */
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Zap, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

export default function AutoApprovalToggle({ brandId }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();

  const settingsQ = useQuery({
    queryKey: qk.ownerBrandSettings(brandId),
    queryFn: () => api.ownerGetBrandSettings(token, brandId),
    enabled: !!token && !!brandId,
  });

  const mut = useMutation({
    mutationFn: (enabled) => api.ownerPatchBrandAutoApproval(token, brandId, { enabled }),
    onMutate: async (enabled) => {
      await qc.cancelQueries({ queryKey: qk.ownerBrandSettings(brandId) });
      const prev = qc.getQueryData(qk.ownerBrandSettings(brandId));
      qc.setQueryData(qk.ownerBrandSettings(brandId), (old) =>
        old ? { ...old, auto_approval_enabled: enabled } : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.ownerBrandSettings(brandId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.ownerBrandSettings(brandId) });
      qc.invalidateQueries({ queryKey: ['owner-receipt-claims-counts'] });
    },
  });

  const enabled = !!settingsQ.data?.auto_approval_enabled;
  const minConf = settingsQ.data?.auto_approval_min_confidence ?? 0.95;

  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#15151B] p-4"
      data-testid="auto-approval-toggle"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={14} className="text-gold" />
            <span className="text-sm font-semibold text-white">Auto-approve eligible claims</span>
          </div>
          <div className="text-xs text-white/50 leading-relaxed">
            When ON, incoming claims meeting the strict eligibility bar
            (GREEN flag · confidence ≥ {Math.round(minConf * 100)}% · no
            duplicate signals · all fields present) are auto-approved
            immediately after AI analysis. All block paths still apply.
          </div>
        </div>
        <button
          type="button"
          onClick={() => mut.mutate(!enabled)}
          disabled={settingsQ.isLoading || mut.isPending}
          data-testid="auto-approval-toggle-switch"
          className={
            'relative inline-flex items-center h-6 w-11 rounded-full transition-colors shrink-0 ' +
            (enabled ? 'bg-gold' : 'bg-white/15') +
            (mut.isPending ? ' opacity-70 cursor-wait' : '')
          }
        >
          <span
            className={
              'absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition-transform ' +
              (enabled ? 'translate-x-5' : 'translate-x-0')
            }
          />
          {mut.isPending ? (
            <Loader2 size={10} className="absolute inset-0 m-auto animate-spin text-black" />
          ) : null}
        </button>
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-white/40 pt-3 border-t border-white/5">
        <span>Minimum AI confidence</span>
        <span className="font-mono text-white/70">{Math.round(minConf * 100)}%</span>
      </div>
    </div>
  );
}
